import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
    ApiException,
    AuthenticationService,
    MessageBar,
    VirtualizedComboBox,
} from '@eta/components';
import {
    DefaultButton,
    Icon,
    IDropdownOption,
    IIconProps,
    initializeIcons,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ServiceCreator } from '../Service/client';
import { StatusTypes, TaxpayerRepresentationDto, UpdateSelectedTaxpayerDto } from '../Service/proxy';

import { loadTaxpayerProfile } from '../../TaxpayerProfile/Redux/Actions';

import './SwitchTaxpayers.scss';
import '../../WaveWave.svg';
import { ReduxState } from '../reducers';
import { isEmptyText } from '../../helpers/utils';
// import { UIHelpers } from '../../helpers/UIHelpers';

initializeIcons((window as any).REACT_APP_Fabric_Icons_CDN);;
const switchIcon: IIconProps = { iconName: 'Switch' };
const backIcon: IIconProps = { iconName: 'Back' };

export interface SwitchTaxpayerProps {
    authService: AuthenticationService;
    applicationLogo?: string;
}

const mapStateToProps = (state: ReduxState) => ({
    currentlanguage: state.reduxlanguage.language,
});

const mapDispatchToProps = {
    dispatchLoadTaxpayerProfile: loadTaxpayerProfile,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const SwitchTaxpayer: FC<
    PropsFromRedux & PropsWithChildren<SwitchTaxpayerProps>
> = (props: PropsFromRedux & PropsWithChildren<SwitchTaxpayerProps>) => {
    const {
        authService,
        applicationLogo,
        children,
        dispatchLoadTaxpayerProfile,
        currentlanguage
    } = props;
    const { t } = useTranslation(['common']);
    const [taxpayerName, setTaxpayerName] = useState<string>('');
    const [taxpayerID, setTaxpayerID] = useState<number>(0);
    const [taxpayerRIN, setTaxpayerRIN] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const history = useHistory();
    const [taxpayerNamesLookups, setTaxpayerNamesLookups] = useState<
        IDropdownOption[] | undefined
    >(undefined);
    const [error, setError] = useState<string>();

    const getTaxpayerName = (taxpayer: TaxpayerRepresentationDto) => {

        let name;
        const nameEn = taxpayer.taxpayerNameEn;
        const nameAr = taxpayer.taxpayerNameAr;
        if (currentlanguage.langIso4.toLowerCase().startsWith('en')) {
            name = isEmptyText(nameEn) ? nameAr : nameEn;
        } else {
            name = isEmptyText(nameAr) ? nameEn : nameAr;
        }
        return name;
    };

    useEffect(() => {
        const bindingCall = async () => {
            let arr: IDropdownOption[] = [];
            const client = ServiceCreator.getUserProfileAdminClient();
            const response = await client.getCurrentUserDetails();
            const taxPayerLookups = response.taxpayerRepresentations ?? [];
            const currentDate = new Date();
            const curentTaxPayerID = parseInt(
                authService?.currentUser?.profile?.TaxId ?? '0',
                10
            );
            
            const activeTaxPayerLookups = taxPayerLookups.filter(
                (taxPayer) =>
                    taxPayer.taxpayerActive &&
                    taxPayer.status === StatusTypes._1 &&
                    taxPayer.taxpayerID !== curentTaxPayerID &&
                    taxPayer.dateActivated !== undefined &&
                    currentDate > taxPayer.dateActivated &&
                    (taxPayer.dateDeactivated === undefined ||
                        currentDate < taxPayer.dateDeactivated)
            );
            arr =
                activeTaxPayerLookups.map<IDropdownOption>(
                    (c) =>
                        ({
                            key: c.taxpayerID ?? 0,
                            text: getTaxpayerName(c) ?? '',
                            data: c.registrationNumber
                        } ?? [])
                ) ?? [];
            setTaxpayerNamesLookups(arr);
        };
        bindingCall();
    }, []);
    const onSwitchTaxpayerClicked = async () => {
        setIsLoading(true);
        try {
            const client = ServiceCreator.getUserProfileAdminClient();
            const request = new UpdateSelectedTaxpayerDto();
            request.taxpayerId = taxpayerID;
            const response = await client.switchTaxpayer(request);
            if (response?.isSuccess) {
                try {
                    localStorage.setItem('RIN', taxpayerRIN || '');
                    await authService.refreshToken();
                    dispatchLoadTaxpayerProfile();
                } catch(ex){
                    setError(ex); 
                }
                history.push('/');
                setIsLoading(false);
            }
        } catch (ex) {
            const exception = ex as ApiException;
            setError(exception.serviceError?.toString() ?? '');
            setIsLoading(false);
        }
    };

    const onBackClicked = async () => {
        history.go(-1);
    };

    const isSwitchEnabled = () => {
        return taxpayerName;
    };

    return children ? (
        <>{children}</>
    ) : (
        <div className="switchTaxpayerPage">
            <div className="cP-page">
                <img
                    className="img-logo"
                    src={applicationLogo}
                    alt="Application logo"
                />
                <div className="panel">
                    <div className="changePasswordContent">
                        <div className="changePasswordHeader">
                            {error && (
                                <MessageBar
                                    messageBarType={MessageBarType.error}
                                    isMultiline={false}
                                    onDismiss={() => setError('')}
                                    className="RelativeMessageBar"
                                >
                                    {error}
                                </MessageBar>
                            )}
                            <h3>
                                <Icon iconName="Switch" /> {t('switchTaxpayer')}
                            </h3>
                        </div>
                        <div>
                            <VirtualizedComboBox
                                options={taxpayerNamesLookups ?? []}
                                selectedKey={taxpayerID}
                                onChange={(_, option) => {
                                    const selectedValue = option?.text ?? '';
                                    const selectedId =
                                        Number(option?.key) ?? null;
                                    setTaxpayerID(selectedId);
                                    setTaxpayerName(selectedValue);
                                    setTaxpayerRIN(option?.data);
                                }}
                                errorMessage={error ?? ''}
                            />
                        </div>
                        <br />
                        <div className="btn_switchTaxPayer">
                            <DefaultButton
                                className="btn-cancel"
                                onClick={onBackClicked}
                                iconProps={backIcon}
                                text={t('common:back')}
                            />
                            <PrimaryButton
                                className="btn-save"
                                onClick={onSwitchTaxpayerClicked}
                                disabled={!isSwitchEnabled() || isLoading}
                                iconProps={switchIcon}
                                text={t('common:switch')}
                            >
                                {isLoading && (
                                    <Spinner size={SpinnerSize.small} />
                                )}
                            </PrimaryButton>
                        </div>
                    </div>
                    <div className="wavePattern">
                        <img
                            className="waveImg"
                            src="/WaveWave.svg"
                            alt="Wave"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
export default connector(SwitchTaxpayer);
